import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'
import {
  Invoice,
  PurchaseOrder,
  WebApiPurchaseOrder,
  SalesTax,
  LineDataBySupplier,
} from '$models'
import {
  invoiceService,
  purchaseOrderService,
  salesTaxService,
} from '$services'
import { exceptionMessageHandler } from '$utils'
interface InvoiceState {
  initialInvoice: Invoice | null
  invoice: Invoice | null
  salesTax: SalesTax[]
  purchaseOrders: PurchaseOrder[]
  linkedPurchaseOrder: PurchaseOrder | null
  lineDataBySupplier: LineDataBySupplier
  isGridLoading: boolean
}

export interface InvoiceSlice extends InvoiceState {
  /**
   * Get an invoice
   * @param id the id of the invoice
   */
  getInvoice: (id: string) => Promise<Invoice | null>

  /**
   * Get an initial invoice
   * @param id the id of the invoice
   */
  getInitialInvoice: (id: string) => Promise<void>

  /**
   * Get sales tax
   */
  getSalesTax: () => Promise<SalesTax[]>

  /**
   * Get a purchase order
   */
  getPurchaseOrders: (purchaseOrderNumber?: string) => Promise<void>

  /**
   * GetWebAPI purchase order
   */
  getWebApiPurchaseOrder: (id: string) => Promise<WebApiPurchaseOrder | null>

  /**
   * Get a purchase order
   */
  getPurchaseOrder: (id: string) => Promise<PurchaseOrder | null>

  /**
   * Get a purchase order
   */
  getLinkedPurchaseOrder: (id?: string) => Promise<void>

  /**
   * Update a purchase order
   */
  updatePurchaseOrder: (purchaseOrder: WebApiPurchaseOrder) => Promise<void>

  /**
   * Update an invoice
   * @param id the id of the invoice
   */
  updateInvoice: (invoice: Invoice) => Promise<Invoice | null>

  /**
   * Remove an invoice
   * @param id the id of the invoice
   */
  removeInvoice: (invoice: Invoice) => Promise<boolean>

  setLinkedPurchaseOrder: (purchaseOrder: PurchaseOrder | undefined) => void

  setLineDataBySupplier: (lineDataBySupplier: LineDataBySupplier) => void

  setIsGridLoading: (bool: boolean) => void

  setInvoice: (invoice: Invoice | null) => void
}

const initialState: InvoiceState = {
  invoice: null,
  initialInvoice: null,
  salesTax: [],
  purchaseOrders: [],
  linkedPurchaseOrder: null,
  lineDataBySupplier: {},
  isGridLoading: false,
}

const createInvoiceSlice: StateCreator<
  InvoiceSlice & SharedSlice,
  [],
  [],
  InvoiceSlice
> = (set, get) => ({
  ...initialState,

  setInvoice: (invoice: Invoice | null): void => {
    set(() => ({
      invoice,
    }))
  },

  getInvoice: async (id: string): Promise<Invoice | null> => {
    set(() => ({ isLoading: true }))
    try {
      const invoice = await invoiceService.getInvoice(id)
      set(() => ({
        invoice,
        isLoading: false,
      }))
      return invoice
    } catch (error) {
      set(() => ({
        invoice: undefined,
        isLoading: false,
      }))
      exceptionMessageHandler(error, 'Failed to retrieve invoice')
      return null
    }
  },

  getInitialInvoice: async (id: string): Promise<void> => {
    try {
      const initialInvoice = await invoiceService.getInitialInvoice(id)
      set(() => ({
        initialInvoice,
      }))
    } catch (error) {
      set(() => ({
        initialInvoice: undefined,
      }))
      exceptionMessageHandler(error, 'Failed to retrieve the initial invoice')
    }
  },

  getSalesTax: async (): Promise<SalesTax[]> => {
    try {
      const salesTax = await salesTaxService.getAllSalesTaxRates()
      set(() => ({
        salesTax,
      }))
      return salesTax
    } catch (error) {
      set(() => ({
        salesTax: [],
      }))
      exceptionMessageHandler(error, 'Failed to retrieve sales Tax')
      return []
    }
  },

  getPurchaseOrders: async (purchaseOrderNumber?: string): Promise<void> => {
    return purchaseOrderService
      .getPurchaseOrders(purchaseOrderNumber)
      .then((purchaseOrders) => {
        // we add here the purchase orders that not exists in the state yet
        const statePurchaseOrders = get().purchaseOrders
        purchaseOrders.forEach((purchaseOrder) => {
          if (
            !statePurchaseOrders.some(
              (statePurchaseOrder) => statePurchaseOrder.id === purchaseOrder.id
            )
          ) {
            statePurchaseOrders.push(purchaseOrder)
          }
        })
        set(() => ({
          purchaseOrders: [...statePurchaseOrders],
        }))
      })
      .catch((error) => {
        exceptionMessageHandler(error, 'Failed to retrieve purchase orders')
        set(() => ({
          purchaseOrders: [],
        }))
      })
  },

  getWebApiPurchaseOrder: async (
    id: string
  ): Promise<WebApiPurchaseOrder | null> => {
    set(() => ({ isLoading: true }))
    try {
      return await purchaseOrderService.getWebApiPurchaseOrder(id)
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to retrieve a purchase order')
      return null
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  getPurchaseOrder: async (id: string): Promise<PurchaseOrder | null> => {
    set(() => ({ isLoading: true }))
    try {
      const purchaseOrder = await purchaseOrderService.getPurchaseOrder(id)
      const statePurchaseOrders = get().purchaseOrders
      if (
        purchaseOrder &&
        !statePurchaseOrders.some(
          (statePurchaseOrder) => statePurchaseOrder.id === purchaseOrder.id
        )
      ) {
        statePurchaseOrders.push(purchaseOrder)
        set(() => ({
          purchaseOrders: [...statePurchaseOrders],
        }))
      }
      return purchaseOrder
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to retrieve a purchase order')
      return null
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  getLinkedPurchaseOrder: async (id?: string): Promise<void> => {
    try {
      set(() => ({ isLoading: true }))
      const linkedPurchaseOrder = id ? await get().getPurchaseOrder(id) : null
      set(() => ({
        linkedPurchaseOrder,
      }))
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to retrieve a purchase order')
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  updatePurchaseOrder: async (
    purchaseOrder: WebApiPurchaseOrder
  ): Promise<void> => {
    set(() => ({ isLoading: true }))
    try {
      await purchaseOrderService.updatePurchaseOrder(purchaseOrder)
    } catch (error) {
      exceptionMessageHandler(error, 'Failed to update a purchase order')
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  updateInvoice: async (invoice: Invoice): Promise<Invoice | null> => {
    set(() => ({ isLoading: true }))
    return invoiceService
      .updateInvoice(invoice)
      .then((invoice) => {
        set(() => ({
          invoice,
        }))
        return invoice
      })
      .catch((error) => {
        exceptionMessageHandler(error, 'Failed to update invoice')
        return null
      })
      .finally(() => {
        set(() => ({ isLoading: false }))
      })
  },

  removeInvoice: async (invoice: Invoice): Promise<boolean> => {
    set(() => ({ isLoading: true }))
    // attempt to remove the invoice via the API
    // Note we dont:
    // - update the local state to remove the invoice, so that if the API delete
    //   fails we haven't lost the information about the invoice
    // - set isLoading back to false once we've completed the call successfully,
    //   this is because we expect the caller to have navigated to a page that
    //   will do it's own loading and we don't want a flash of one loading
    //   finishing and another one starting
    let ok = true
    await invoiceService.removeInvoice(invoice).catch((error) => {
      ok = false
      set(() => ({ isLoading: false }))
      exceptionMessageHandler(error, 'Failed to delete invoice')
    })

    return ok
  },

  setLinkedPurchaseOrder: (
    linkedPurchaseOrder: PurchaseOrder | undefined
  ): void => {
    set(() => ({
      linkedPurchaseOrder: linkedPurchaseOrder,
    }))
  },

  setLineDataBySupplier: (lineDataBySupplier: LineDataBySupplier): void => {
    set(() => ({
      lineDataBySupplier,
    }))
  },

  setIsGridLoading: (isGridLoading: boolean): void => {
    set(() => ({
      isGridLoading,
    }))
  },
})

export default createInvoiceSlice
